import React, { PropsWithoutRef, useCallback, useMemo } from 'react';
import styled from '@tanium/react-emotion';
import { CheckIcon, LineIcon } from '@tanium/react-graphics';
import { useUsingMouse } from '@tanium/react-mouse-interaction-context';
import { useAggregatedRef } from '@tanium/react-use-aggregated-ref';
import { css } from '@tanium/style-system';
import { ThemeRadii } from '@tanium/theme-data';
import {
  ActiveBorder,
  ActiveFill,
  Border,
  CheckedActiveFill,
  CheckedFill,
  Checkmark,
  DisabledBorder,
  DisabledCheckedCheckmark,
  DisabledCheckedFill,
  DisabledFill,
  Fill,
  FocusShadow,
  HoverBorder,
  InvalidBorder,
} from './themeDefinitions';

export type CheckboxChangeEvent = React.ChangeEvent<HTMLInputElement>;

export interface Props extends PropsWithoutRef<JSX.IntrinsicElements['input']> {
  /**
   * Use this prop to control whether the checkbox is currently indeterminate.
   */
  indeterminate?: boolean;
  /**
   * Whether the checkbox should be displayed in an invalid (error) state.
   */
  invalid?: boolean;
}

/**
 * width and height measurement of the checkbox (in pixels)
 */
export const CHECKBOX_SIZE = 16;

/**
 * width and height measurement of the indeterminate indicator (in pixels)
 */
export const INDETERMINATE_SIZE = 12;

/**
 * Padding on each side of the check mark within the checkbox (in pixels)
 */
const CHECKBOX_PADDING = 2;

/**
 * This span wraps around everything in this component and ensures
 * that this component flows properly in the DOM
 */
const Wrapper = styled('span')({
  position: 'relative',
  display: 'inline-block',
  textAlign: 'left',
  width: CHECKBOX_SIZE,
  height: CHECKBOX_SIZE,
});

/**
 * This span wraps just the fake checkbox portion of the component;
 * it is what the user sees and it does not include the hidden input element.
 * The user clicks right through this element onto the invisible input element below.
 */
const FakeCheckboxWrapper = styled('span')({
  pointerEvents: 'none',
});

const FakeCheckbox = styled('div', {
  shouldForwardProp: (p) => p !== 'usingMouse',
})<{
  usingMouse: boolean;
}>(
  {
    position: 'absolute',
    width: CHECKBOX_SIZE,
    height: CHECKBOX_SIZE,
  },
  css({
    border: 1,
    borderRadius: ThemeRadii.Checkbox,
    bg: Fill,
    borderColor: Border,
  }),
);

const CheckmarkWrapper = styled(CheckIcon)(
  {
    position: 'absolute',
    width: CHECKBOX_SIZE,
    height: CHECKBOX_SIZE,
    padding: CHECKBOX_PADDING,
    overflow: 'visible',
    visibility: 'hidden',
  },
  css({ color: Checkmark }),
);

const IndeterminateWrapper = styled(LineIcon)(
  {
    left: '2px',
    overflow: 'visible',
    position: 'absolute',
    top: '2px',
  },
  css({ color: Checkmark, size: INDETERMINATE_SIZE }),
);

const BOX_SELECTOR = `+ ${FakeCheckboxWrapper} > ${FakeCheckbox}`;
const MARK_SELECTOR = `+ ${FakeCheckboxWrapper} > ${CheckmarkWrapper}`;
const INDETERMINATE_SELECTOR = `+ ${FakeCheckboxWrapper} > ${IndeterminateWrapper}`;

/*
 * The checkbox input element is completely transparent
 * and placed behind the custom styled fake checkbox.
 */
const HiddenInput = styled('input', {
  shouldForwardProp: (p) => ['indeterminate', 'invalid', 'usingMouse'].indexOf(p) === -1,
})<{ indeterminate: boolean; invalid: boolean; usingMouse: boolean }>(
  {
    left: 0,
    opacity: 0,
    position: 'absolute',
    top: 0,
    width: CHECKBOX_SIZE,
    height: CHECKBOX_SIZE,
    // override top margin on input[type="checkbox"] from bootstrap
    '&&': { margin: 0 },
  },
  // attribute selectors are necessary to target FakeCheckboxWrapper, which suppresses pointer events
  ({ indeterminate }) =>
    css({
      ':checked': {
        [BOX_SELECTOR]: {
          bg: CheckedFill,
          borderColor: 'transparent',
        },
        [MARK_SELECTOR]: {
          visibility: 'visible',
        },
        ':active': {
          [BOX_SELECTOR]: {
            bg: CheckedActiveFill,
          },
        },
      },

      ':disabled': {
        [BOX_SELECTOR]: {
          cursor: 'not-allowed',
          bg: indeterminate ? DisabledCheckedFill : DisabledFill,
          borderColor: DisabledBorder,
        },
        ':checked': {
          [BOX_SELECTOR]: {
            bg: DisabledCheckedFill,
          },
          [MARK_SELECTOR]: {
            color: DisabledCheckedCheckmark,
          },
        },
      },
    }),
  ({ invalid, usingMouse }) => [
    css(
      invalid
        ? {
            // override specificity of :disabled
            '&&': {
              [BOX_SELECTOR]: {
                borderColor: InvalidBorder,
              },
            },
          }
        : {
            [':not(:disabled)' as string]: {
              ':hover': {
                [BOX_SELECTOR]: {
                  borderColor: HoverBorder,
                },
              },
              ':active': {
                [BOX_SELECTOR]: {
                  borderColor: ActiveBorder,
                },
              },
            },
          },
    ),
    css({
      ':active': {
        [BOX_SELECTOR]: {
          bg: ActiveFill,
        },
      },
    }),
    !usingMouse &&
      css({
        ':focus': {
          [BOX_SELECTOR]: {
            boxShadow: FocusShadow,
          },
        },
      }),
  ],
  // We can't use pseudo selectors for indeterminate, so we check the prop passed into this component
  // and apply these styles last in case they need to override some earlier-applied styling.
  ({ indeterminate }) =>
    indeterminate &&
    css({
      [BOX_SELECTOR]: {
        bg: CheckedFill,
        borderColor: 'transparent',
      },
      ':active': {
        [BOX_SELECTOR]: {
          bg: CheckedActiveFill,
        },
      },
      ':disabled': {
        [INDETERMINATE_SELECTOR]: {
          color: DisabledCheckedCheckmark,
        },
      },
    }),
);

/**
 * When `indeterminate` is true, ignore `checked=true`.
 */
const getCheckedValue = (checked?: boolean, indeterminate?: boolean) => {
  if (checked && indeterminate) {
    // The checkbox cannot be both indeterminate and checked at the same time.
    // Indeterminate wins.
    return false;
  }

  return checked;
};

/**
 * A styled wrapper for `<input type="checkbox" />`.
 *
 * In addition to standard input element attributes, `Checkbox` supports:
 * - an `invalid` prop for specifying when the checkbox is in an invalid state
 * - an `indeterminate` prop for managing the `indeterminate` attribute
 *   declaratively
 *
 * To show a checkbox with an associated label, use
 * [CheckboxWithLabel](https://pages.git.corp.tanium.com/eng/ufe/?path=/docs/core-ui-components-checkboxwithlabel)
 * instead.
 *
 * To show a tooltip when the user hovers over a checkbox, use
 * [TooltippedCheckbox](https://pages.git.corp.tanium.com/eng/ufe/?path=/docs/core-ui-components-tooltippedcheckbox)
 * instead.
 */
export const Checkbox = React.forwardRef<HTMLInputElement, Props>(
  ({ checked, indeterminate = false, invalid, ...props }, ref) => {
    const usingMouse = useUsingMouse();

    const checkedProp = getCheckedValue(checked, indeterminate);

    // Set the "indeterminate" state on the DOM element programmatically because there is no such
    // attribute in HTML nor in React.
    const innerRef = useCallback(
      (node: HTMLInputElement) => {
        if (node !== null) {
          // eslint-disable-next-line no-param-reassign
          node.indeterminate = indeterminate;
        }
      },
      [indeterminate],
    );

    const refs = useMemo(() => [ref, innerRef], [ref, innerRef]);
    const aggregateRef = useAggregatedRef(refs);

    return (
      <Wrapper>
        <HiddenInput
          {...props}
          // If the consumer is using this component in an uncontrolled way, this prop is not set.
          aria-checked={indeterminate ? 'mixed' : checkedProp}
          checked={checkedProp}
          indeterminate={indeterminate}
          ref={aggregateRef}
          type="checkbox"
          invalid={!!invalid}
          usingMouse={usingMouse}
        />
        <FakeCheckboxWrapper aria-hidden="true">
          <FakeCheckbox usingMouse={usingMouse} />
          {/* If "indeterminate" and "checked", only show the "indeterminate" indicator. */}
          {indeterminate ? <IndeterminateWrapper /> : <CheckmarkWrapper />}
        </FakeCheckboxWrapper>
      </Wrapper>
    );
  },
);
